import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Banks } from '@core/enums';
import { CaseDetails, LeaveHoursBanksBuckets } from '@core/models';
import { EmployeeEntitlement } from '@core/models/leave-admin/employees/employee-entitlement.model';
import { EmployeeRecord } from '@core/models/leave-admin/employees/employee-record.model';
import { LeaveHoursDelete } from '@core/models/leave-admin/employees/leave-hours-form.model';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { LayoutService } from '@core/services';
import { EmployeeRecordStoreService } from '@core/services/employee-record-store.service';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { ManageEmployeesService } from '@core/services/manage-employees.service';
import { CalcEntitlementDialogComponent, CalcEntitlementDialogComponentData } from '@modules/dialogs/calc-entitlement-dialog/calc-entitlement-dialog.component';
import { DeleteConfirmationComponent } from '@modules/dialogs/delete-confirmation/delete-confirmation.component';
import { 
  EditLeaveHoursComponent, 
  EditLeaveHoursComponentData, 
  EditLeaveHoursComponentMode } from '@modules/dialogs/edit-leave-hours/edit-leave-hours.component';
import { TitleMessageDialog } from '@modules/dialogs/title-message-dialog/title-message-dialog.component';
import { UpdateConfirmationComponent } from '@modules/dialogs/update-confirmation/update-confirmation.component';
import { ReplaceBankNamesPipe } from '@shared/pipes/replace-bank-name.pipe';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { appRoutePaths } from '@core/routes/route-paths.constants';
import { PendingActionsAlertComponent } from '@modules/dialogs/pending-actions-alert/pending-actions-alert.component';

@Component({
    selector: 'app-employee-entitlements',
    templateUrl: './employee-entitlements.component.html',
    styleUrl: './employee-entitlements.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EmployeeEntitlementsComponent {
  static readonly componentName = 'EmployeeEntitlementsComponent';
  
  @Input() isCaseDetails: boolean = false;
  @Input() panelOpenStateSubject: Observable<boolean>;
  @Input() setThisPanelOpenState$: BehaviorSubject<boolean>;

  @Output() isEditing = new EventEmitter<boolean>();

  // where focus goes after a delete takes its row (and the focused button) away
  @ViewChild('sectionHeader', { read: ElementRef }) sectionHeader: ElementRef<HTMLElement>;

  public sysText: any;
  public unsavedChangesSysText: any;
  public updateConfSysText: any;
  public showEntitlements: boolean;
  public canChange: boolean;
  public canCalc: boolean = false;
  public isLoadingEntitlementDialog$: Subject<boolean> = new Subject<boolean>();
  public isLoadingCalcEntitlementDialog$: Subject<boolean> = new Subject<boolean>();
  public _ee: MatTableDataSource<EmployeeEntitlement> = new MatTableDataSource<EmployeeEntitlement>();
  public displayedColumns: string[] = ['effectiveDate', 'bank', 'totalHours', 'casesWithLeaves', 'leavesTaken', 'leaveHoursTaken', 'icons'];
  public isSubmittingId: number = null;
  public leaveHoursBanks: LeaveHoursBanksBuckets;
  public banks = Banks;
  public panelOpenState: boolean = true;
  public eeType: EmployeeEntitlement;
  public caseNumber: number;

  private destroy$: Subject<void> = new Subject<void>();
  public routes: typeof appRoutePaths = appRoutePaths;

  // one data source whose data is replaced, plus trackBy, so a refresh keeps the table's rows (and the control that has
  // focus) instead of rebuilding them
  set dataSource(dataSource: EmployeeEntitlement[]) {
    this._ee.data = dataSource;
  }

  public trackByEntitlementId(index: number, ee: EmployeeEntitlement): number {
    return ee.employeeBankTotalHoursId;
  }

  get isMobile$(): Observable<boolean> {
    return this.layoutService.isMobile$();
  }

  get isMicro$(): Observable<boolean> {
    return this.layoutService.isMicro$();
  }

  constructor(
    private manageEmployeesService: ManageEmployeesService,
    private leaveAdminService: LeaveAdminService,
    private layoutService: LayoutService,
    private employeeRecordStore: EmployeeRecordStoreService,
    private caseDetailsStore: LeaveAdminStoreService,
    private dialog: MatDialog,
    private replaceBankPipe: ReplaceBankNamesPipe
  ) { }

  ngOnInit() {
    this.setThisPanelOpenState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => this.panelOpenState = res);
      
    // case details
    if (this.isCaseDetails) {
      this.sysText = this.caseDetailsStore.sysText[this.caseDetailsStore.templates.employeeEntitlement];
      this.unsavedChangesSysText = this.caseDetailsStore.sysText[this.caseDetailsStore.templates.unsavedChanges];
      this.updateConfSysText = this.caseDetailsStore.sysText[this.caseDetailsStore.templates.updateConfirmation];

      this.caseDetailsStore.caseDetails$
        .pipe(takeUntil(this.destroy$))
        .subscribe((res)=> {
          if (res != null) {
            this.showEntitlements = res.showEditTotalHours;
            this.canChange = !res.isClosed && res.canEditInfo && res.showEditTotalHours;
            this.canCalc = !res.isClosed && res.canEditInfo && res.showEditTotalHours;
            this.dataSource = res.employeeEntitlements;
            this.leaveHoursBanks = res.leaveHoursBanksBuckets;
            this.caseNumber = res.leaveInformation.caseId;
          }
        });
    // employee record
    } else {
      this.sysText = this.employeeRecordStore.sysText[this.employeeRecordStore.templates.employeeEntitlement];
      this.unsavedChangesSysText = this.employeeRecordStore.sysText[this.employeeRecordStore.templates.unsavedChanges];
      this.updateConfSysText = this.employeeRecordStore.sysText[this.employeeRecordStore.templates.updateConfirmation];

      this.employeeRecordStore.employeeRecord$
        .pipe(takeUntil(this.destroy$))
        .subscribe((res)=> {
          if (res != null) {
            this.showEntitlements = true;
            this.canChange = res.showEditTotalHours;
            this.dataSource = res.employeeEntitlements;
            this.leaveHoursBanks = res.leaveHoursBanks;
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onAddEntitlement(): void {
    if (this.isCaseDetails && this.caseDetailsStore.caseDetails.employeeInformation.isAnonymous) {
      this.showAnonymousCaseMessage();
      return;
    }

    const dialogRef = this.dialog.open<
      EditLeaveHoursComponent, 
      EditLeaveHoursComponentData, 
      CaseDetails | EmployeeRecord>
      (EditLeaveHoursComponent, {
      id: EditLeaveHoursComponent.componentName,
      panelClass: "mat-dialog-container-mobileWidth",
      data: this.getDialogData(EditLeaveHoursComponentMode.add)
    });

    dialogRef.componentInstance.isEditing.subscribe((res) => {
      this.isEditing.emit(res);
    });

    dialogRef.afterClosed()
      .pipe(
        filter((res) => !!res),
        finalize(() => this.isEditing.emit(false)))
      .subscribe(res => {
        if (this.isCaseDetails) {
          this.caseDetailsStore.caseDetails = res as CaseDetails
        } else {
          this.employeeRecordStore.employeeRecord = res as EmployeeRecord;
        }
      });
  }

  public onCalcEntitlement(): void {
    if (this.isCaseDetails && this.caseDetailsStore.caseDetails.employeeInformation.isAnonymous) {
      this.showAnonymousCaseMessage();
      return;
    }
    
    this.isEditing.emit(true);

    const diaogRef = this.dialog.open<
      CalcEntitlementDialogComponent,
      CalcEntitlementDialogComponentData>
      (CalcEntitlementDialogComponent, {
      id: CalcEntitlementDialogComponent.componentName,
      panelClass: "mat-dialog-container-mobileWidth",
      data: { 
        caseId: this.caseDetailsStore.caseDetails.leaveInformation.caseId,
        sysText: this.sysText,
        caseStartDate: this.caseDetailsStore.caseDetails.leaveInformation.startDate
      }
    });

    diaogRef.afterClosed().subscribe(() => {
      this.isEditing.emit(false);
    });
  }

  public onEditEntitlement(ee: EmployeeEntitlement): void {
    const dialogRef = this.dialog.open<
      EditLeaveHoursComponent, 
      EditLeaveHoursComponentData, 
      CaseDetails | EmployeeRecord>
      (EditLeaveHoursComponent, {
      id: EditLeaveHoursComponent.componentName,
      panelClass: "mat-dialog-container-mobileWidth",
      data: this.getDialogData(EditLeaveHoursComponentMode.edit, ee)
    });

    dialogRef.componentInstance.isEditing.subscribe((res) => {
      this.isEditing.emit(res);
    });

    dialogRef.afterClosed()
      .pipe(
        filter((res) => !!res),
        finalize(() => this.isEditing.emit(false)))
      .subscribe(res => {
        if (this.isCaseDetails) {
          this.caseDetailsStore.caseDetails = res as CaseDetails
        } else {
          this.employeeRecordStore.employeeRecord = res as EmployeeRecord;
        }
      });
  }

  public onDeleteEntitlement(id: number): void {
    const dialogConfig: MatDialogConfig = {
      id: DeleteConfirmationComponent.componentName,
      width: '300px',
      disableClose: false,
      data: { sysText: this.isCaseDetails ? 
        this.caseDetailsStore.sysText.deleteConfirmation : 
        this.employeeRecordStore.sysText.deleteConfirmation }
    };

    this.dialog.open(DeleteConfirmationComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.submitDelete(id);
        }
      });
  }

  private submitDelete(id: number, overrideUpdateConf: boolean = false): void {
    if (this.isCaseDetails && this.caseDetailsStore.isActionRunning) {
      const dialogConfig: MatDialogConfig = {
        id: PendingActionsAlertComponent.componentName,
        width: '300px'
      };

      this.dialog.open(PendingActionsAlertComponent, dialogConfig)
        .beforeClosed()
        .subscribe((res: boolean) => {
          if (res) {
            this.submitDelete(id, overrideUpdateConf);
          }
        });
      return;
    }

    this.isSubmittingId = id;

    const dto: LeaveHoursDelete = {
      overrideUpdateConf: overrideUpdateConf,
      employeeId: this.isCaseDetails ? 
        null : 
        this.employeeRecordStore.employeeRecord.employeeId,
      caseId: this.isCaseDetails ? 
        this.caseDetailsStore.caseDetails.leaveInformation.caseId : 
        null,
      employeeBankTotalHoursId: id
    }

    // case details
    if (this.isCaseDetails) {
      this.leaveAdminService.postLeaveHoursDelete(dto)
        .subscribe(res => {
          if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(id, res as UpdateConfirmationDialog);
          } else {
            this.caseDetailsStore.caseDetails = res as CaseDetails;
            this.isSubmittingId = null;
            this.sectionHeader.nativeElement.focus();
          }
        });
    } 
    // employee record
    else {
      this.manageEmployeesService.postLeaveHoursDelete(dto)
        .subscribe(res => {
          if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(id, res as UpdateConfirmationDialog);
          } else {
            this.employeeRecordStore.employeeRecord = res as EmployeeRecord;
            this.isSubmittingId = null;
            this.sectionHeader.nativeElement.focus();
          }
        });
    } 
  }

  private getDialogData(
    mode: EditLeaveHoursComponentMode, 
    ee: EmployeeEntitlement = null): EditLeaveHoursComponentData {
    return {
      mode: mode,
      caseId: this.isCaseDetails ? 
        this.caseDetailsStore.caseDetails.leaveInformation.caseId : 
        null,
      eeId: this.isCaseDetails ? 
        this.caseDetailsStore.caseDetails.employeeInformation.employee?.id as number : 
        this.employeeRecordStore.employeeRecord.employeeId,
      employeeEntitlement: ee,
      sysText: this.sysText,
      unsavedChangesSysText: this.unsavedChangesSysText,
      updateConfSysText: this.updateConfSysText,
      leaveHoursBanksBuckets: this.leaveHoursBanks
    }
  }

  private openUpdateConfirmationDialog(id: number, updateConfirmationDialog: UpdateConfirmationDialog): void {
    const objString = JSON.stringify(updateConfirmationDialog);
    updateConfirmationDialog = JSON
      .parse(this.replaceBankPipe
      .transform(objString, this.isCaseDetails ? 
        this.caseDetailsStore.caseDetails.leaveHoursBanksBuckets :
        this.employeeRecordStore.employeeRecord.leaveHoursBanks
      ));
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText:  this.isCaseDetails ? 
          this.caseDetailsStore.sysText[this.caseDetailsStore.templates.updateConfirmation] :
          this.employeeRecordStore.sysText[this.employeeRecordStore.templates.updateConfirmation],
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.submitDelete(id, true);
        } else {
          this.isSubmittingId = null;
        }
      });
  }

  private showAnonymousCaseMessage() {
      const dialogConfig: MatDialogConfig = {
        id: TitleMessageDialog.componentName,
        width: '300px',
        disableClose: false,
        data: { sysText: {
          titleMessageDialog_title: this.caseDetailsStore.sysText[this.caseDetailsStore.templates.anonymousCaseSetEmployee].anonymousCaseSetEmployee_title,
          titleMessageDialog_message: this.caseDetailsStore.sysText[this.caseDetailsStore.templates.anonymousCaseSetEmployee].anonymousCaseSetEmployee_message,
          titleMessageDialog_button: this.caseDetailsStore.sysText[this.caseDetailsStore.templates.anonymousCaseSetEmployee].anonymousCaseSetEmployee_button
        }}
      };

      this.dialog.open(TitleMessageDialog, dialogConfig);
  }
}
